import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from "@angular/core";
import { Meeting } from "@common/ADAPT.Common.Model/organisation/meeting";
import { Team } from "@common/ADAPT.Common.Model/organisation/team";
import { ArrayUtilities } from "@common/lib/utilities/array-utilities";
import { IDxListSelectionChangedEvent } from "@common/ux/dx.types";
import ArrayStore from "devextreme/data/array_store";
import DataSource from "devextreme/data/data_source";
import { InitializedEvent } from "devextreme/ui/list";
import { map, Observable } from "rxjs";
import { MeetingsService } from "../meetings.service";

@Component({
    selector: "adapt-recent-meeting-selector",
    templateUrl: "./recent-meeting-selector.component.html",
    standalone: false,
})
export class RecentMeetingSelectorComponent implements OnChanges {
    @Input() public currentMeeting?: Meeting;
    @Input() public currentTeam?: Team; // need this for creating team template
    @Output() public meetingSelected = new EventEmitter<Meeting>();

    public recentMeetingsDataSource$?: Observable<DataSource>;

    public constructor(
        private meetingsService: MeetingsService,
    ) { }

    public ngOnChanges(changes: SimpleChanges) {
        if (changes.currentMeeting) {
            this.updateRecentMeetings();
        }
    }

    public onListInitialized(e: InitializedEvent) {
        // Note: tried to do this onGroupRendered as well but that ended collapsing the group even after selecting it, hiding away user selection
        // - so leave it on onInitialized and only collapse once - have to live with groups all expanded again after search (Steve OK with that)
        if (e.component) {
            const component = e.component;
            setTimeout(() => { // this is from onInitialized callback -> cannot collapse yet - need to wait for the list to be fully initialized
                const groupedItems = component.getDataSource().items();
                // only leave the first group expanded (which is the current team - if not, first team alphabetically or else it looks as if there are no meeting to select)
                for (let i = 1; i < groupedItems.length; i++) {
                    component.collapseGroup(i);
                }
            });
        }
    }

    public onSelectionChanged(e: IDxListSelectionChangedEvent<Meeting>) {
        this.meetingSelected.emit(e.addedItems[0]);
    }

    private updateRecentMeetings() {
        this.recentMeetingsDataSource$ = this.meetingsService.getAllRecentMeetings().pipe(
            map((meetings) => {
                // excluding the current meeting
                meetings = meetings.filter((m) => m !== this.currentMeeting && !m.entityAspect.entityState.isAddedModifiedOrDeleted());

                // sort the recent meetings by the current meeting's team, and then by alphabetical team name
                // note: DX forces you to pre-group data in this way to get custom sort
                // https://supportcenter.devexpress.com/ticket/details/t923260/selectbox-how-to-sort-a-grouped-field-based-on-another-field
                const groupedData = ArrayUtilities.groupArrayBy(meetings, (m) => m.team);
                groupedData.sort((a, b) => {
                    if (a.key?.teamId === this.currentMeeting?.teamId || a.key?.teamId === this.currentTeam?.teamId) {
                        return -1;
                    } else if (b.key?.teamId === this.currentMeeting?.teamId || b.key?.teamId === this.currentTeam?.teamId) {
                        return 1;
                    } else {
                        return a.key!.name.localeCompare(b.key!.name);
                    }
                });

                const sortedMeetings = groupedData.flatMap((g) => g.items);
                const mappedTeamNames: { [teamId: number]: string } = {};
                // fixed 6 digits padding in front of team name to keep the order
                groupedData.forEach((g, index) => mappedTeamNames[g.key?.teamId ?? 0] = `${index.toString().padStart(6, "0")}${g.key?.name}`);

                return new DataSource({
                    store: new ArrayStore({
                        data: sortedMeetings,
                        key: "meetingId",
                    }),
                    group: (meeting: Meeting) => mappedTeamNames[meeting.teamId],
                });
            }),
        );
    }
}
